'use client';

import React, {
  memo, useEffect, useMemo, useRef, useState
} from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import {
  Area, AreaChart,
  Bar, BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line, LineChart,
  Pie, PieChart,
  ResponsiveContainer,
  Scatter, ScatterChart,
  Tooltip,
  XAxis, YAxis
} from 'recharts';

import { Icon } from '@/components/icon/icon';

import styles from '../explorer.module.scss';
import { QueryResult } from '../types';
import { classifyColumns, ColumnClass } from '../utils/columnClassification';
import { CHART_COLORS as COLORS, CHART_MAX_ROWS } from '../utils/constants';

type ChartType = 'bar' | 'horizontalBar' | 'line' | 'area' | 'pie' | 'scatter';
type ChartConfig = { type: ChartType; xKey: string; yKeys: string[] };

function detectChartType( classified: Map<string, ColumnClass>, rows: Record<string, unknown>[] ): ChartConfig {
  const dateCols = [ ...classified.entries() ].filter( ( [ , c ] ) => c === 'date' ).map( ( [ k ] ) => k );
  const numericCols = [ ...classified.entries() ].filter( ( [ , c ] ) => c === 'numeric' ).map( ( [ k ] ) => k );
  const catCols = [ ...classified.entries() ].filter( ( [ , c ] ) => c === 'categorical' ).map( ( [ k ] ) => k );

  // Priority 1: date + 1 numeric → area
  if ( dateCols.length > 0 && numericCols.length === 1 ) {
    return { type: 'area', xKey: dateCols[0], yKeys: numericCols };
  }

  // Priority 2: date + 2+ numeric → line
  if ( dateCols.length > 0 && numericCols.length >= 2 ) {
    return { type: 'line', xKey: dateCols[0], yKeys: numericCols };
  }

  // Priority 3: 1 categorical + 1 numeric, ≤10 rows → pie
  if ( catCols.length === 1 && numericCols.length === 1 && rows.length <= 10 ) {
    return { type: 'pie', xKey: catCols[0], yKeys: numericCols };
  }

  // Priority 4: 2+ numeric, 0 categorical → scatter
  if ( numericCols.length >= 2 && catCols.length === 0 && dateCols.length === 0 ) {
    return { type: 'scatter', xKey: numericCols[0], yKeys: [ numericCols[1] ] };
  }

  // Priority 5 & 6: categorical + numeric
  if ( catCols.length > 0 && numericCols.length > 0 ) {
    const avgLen = rows.reduce( ( sum, r ) => sum + String( r[catCols[0]] ?? '' ).length, 0 ) / rows.length;
    if ( avgLen > 15 ) {
      return { type: 'horizontalBar', xKey: catCols[0], yKeys: numericCols };
    }
    return { type: 'bar', xKey: catCols[0], yKeys: numericCols };
  }

  // Fallback: first col as x, rest as y, bar chart
  const allCols = [ ...classified.keys() ];
  if ( allCols.length >= 2 ) {
    return { type: 'bar', xKey: allCols[0], yKeys: allCols.slice( 1 ) };
  }

  return { type: 'bar', xKey: allCols[0] || '', yKeys: [] };
}

const ALL_CHART_TYPE_OPTIONS: { value: ChartType; label: string }[] = [
  { value: 'bar', label: 'Bar' },
  { value: 'horizontalBar', label: 'Horizontal Bar' },
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
  { value: 'pie', label: 'Pie' },
  { value: 'scatter', label: 'Scatter' }
];

function getApplicableChartTypes(
  classified: Map<string, ColumnClass>,
  rowCount: number
): Set<ChartType> {
  const types = new Set<ChartType>();
  const numericCols = [ ...classified.values() ].filter( c => c === 'numeric' ).length;
  const catCols = [ ...classified.values() ].filter( c => c === 'categorical' ).length;
  const dateCols = [ ...classified.values() ].filter( c => c === 'date' ).length;

  // Bar/Horizontal Bar: always available if there's at least 1 numeric column
  if ( numericCols > 0 ) {
    types.add( 'bar' );
    types.add( 'horizontalBar' );
  }

  // Line: needs an ordered axis (date or numeric) + numeric values
  if ( ( dateCols > 0 || numericCols >= 2 ) && numericCols > 0 ) {
    types.add( 'line' );
  }

  // Area: same as line
  if ( ( dateCols > 0 || numericCols >= 2 ) && numericCols > 0 ) {
    types.add( 'area' );
  }

  // Pie: only appropriate for small distinct categories (≤20 rows, at least 1 cat + 1 numeric)
  if ( catCols > 0 && numericCols > 0 && rowCount <= 20 ) {
    types.add( 'pie' );
  }

  // Scatter: needs 2+ numeric columns
  if ( numericCols >= 2 ) {
    types.add( 'scatter' );
  }

  return types;
}

type Props = {
  result: QueryResult | null;
  isExecuting?: boolean;
};

function ChartPanel( { result, isExecuting }: Props ) {
  const [ collapsed, setCollapsed ] = useState( false );
  const [ chartTypeOverride, setChartTypeOverride ] = useState<ChartType | null>( null );
  const [ xKeyOverride, setXKeyOverride ] = useState<string | null>( null );
  const chartRef = useRef<HTMLDivElement>( null );

  // Reset overrides when result changes
  useEffect( () => {
    setChartTypeOverride( null );
    setXKeyOverride( null );
  }, [ result ] );

  // Classify columns once for dropdown options
  const classified = useMemo( () => {
    if ( !result || !result.columns.length ) return new Map<string, ColumnClass>();
    return classifyColumns( result.columns, result.rows );
  }, [ result ] );

  const allColumns = useMemo( () => result?.columns ?? [], [ result ] );

  const { config, chartData, truncated } = useMemo( () => {
    if ( !result || !result.rows.length || !result.columns.length || result.error ) {
      return { config: null, chartData: [], truncated: false };
    }

    const detected = detectChartType( classified, result.rows );

    if ( detected.yKeys.length === 0 ) {
      return { config: null, chartData: [], truncated: false };
    }

    // Apply user X-axis override; Y keys are always auto-detected
    const xKey = xKeyOverride ?? detected.xKey;
    const yKeys = detected.yKeys;

    const effectiveConfig: ChartConfig = {
      type: detected.type,
      xKey,
      yKeys
    };

    const isTruncated = result.rows.length > CHART_MAX_ROWS;
    const data = ( isTruncated ? result.rows.slice( 0, CHART_MAX_ROWS ) : result.rows ).map( row => {
      const entry: Record<string, unknown> = {};
      const xVal = row[xKey];
      entry[xKey] = xVal === null || xVal === undefined ? '(empty)' : xVal;
      for ( const yk of yKeys ) {
        const v = row[yk];
        if ( v === null || v === undefined ) {
          entry[yk] = null;
        } else {
          const n = Number( v );
          entry[yk] = Number.isFinite( n ) ? n : null;
        }
      }
      return entry;
    } );

    return { config: effectiveConfig, chartData: data, truncated: isTruncated };
  }, [ result, classified, xKeyOverride ] );

  // Filter chart types to only show appropriate ones for the data
  const applicableTypes = useMemo( () => {
    if ( !result || !result.rows.length ) return new Set<ChartType>( [ 'bar' ] );
    return getApplicableChartTypes( classified, result.rows.length );
  }, [ result, classified ] );

  const chartTypeOptions = useMemo(
    () => ALL_CHART_TYPE_OPTIONS.filter( opt => applicableTypes.has( opt.value ) ),
    [ applicableTypes ]
  );

  const activeType = chartTypeOverride && applicableTypes.has( chartTypeOverride )
    ? chartTypeOverride
    : config?.type ?? 'bar';

  const hasChart = !!config;

  const renderChart = () => {
    if ( !config ) return null;

    const xKey = config.xKey;
    const yKeys = config.yKeys;
    const type = activeType;

    if ( type === 'pie' ) {
      return (
        <PieChart>
          <Pie
            data={ chartData }
            dataKey={ yKeys[0] }
            nameKey={ xKey }
            cx="50%"
            cy="50%"
            outerRadius="70%"
            label={ ( entry ) => String( ( entry as unknown as Record<string, unknown> )[xKey] ?? '' ) }
          >
            { chartData.map( ( _, i ) => (
              <Cell key={ i } fill={ COLORS[i % COLORS.length] } />
            ) ) }
          </Pie>
          <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
          <Legend />
        </PieChart>
      );
    }

    if ( type === 'scatter' ) {
      return (
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={ xKey } name={ xKey } tick={ { fontSize: 12 } } />
          <YAxis dataKey={ yKeys[0] } name={ yKeys[0] } tick={ { fontSize: 12 } } />
          <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
          <Legend />
          <Scatter name={ `${ xKey } vs ${ yKeys[0] }` } data={ chartData } fill={ COLORS[0] } />
        </ScatterChart>
      );
    }

    if ( type === 'line' ) {
      return (
        <LineChart data={ chartData }>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={ xKey } tick={ { fontSize: 12 } } />
          <YAxis tick={ { fontSize: 12 } } />
          <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
          <Legend />
          { yKeys.map( ( yk, i ) => (
            <Line key={ yk } type="monotone" dataKey={ yk } stroke={ COLORS[i % COLORS.length] } dot={ false } />
          ) ) }
        </LineChart>
      );
    }

    if ( type === 'area' ) {
      return (
        <AreaChart data={ chartData }>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={ xKey } tick={ { fontSize: 12 } } />
          <YAxis tick={ { fontSize: 12 } } />
          <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
          <Legend />
          { yKeys.map( ( yk, i ) => (
            <Area key={ yk } type="monotone" dataKey={ yk } stroke={ COLORS[i % COLORS.length] } fill={ COLORS[i % COLORS.length] } fillOpacity={ 0.3 } />
          ) ) }
        </AreaChart>
      );
    }

    if ( type === 'horizontalBar' ) {
      return (
        <BarChart data={ chartData } layout="vertical" barCategoryGap="15%" barGap={ 2 }>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tick={ { fontSize: 12 } } />
          <YAxis dataKey={ xKey } type="category" tick={ { fontSize: 12 } } width={ 120 } />
          <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
          <Legend />
          { yKeys.map( ( yk, i ) => (
            <Bar key={ yk } dataKey={ yk } fill={ COLORS[i % COLORS.length] } name={ yk } />
          ) ) }
        </BarChart>
      );
    }

    // Default: bar (grouped side-by-side for multiple Y keys)
    return (
      <BarChart data={ chartData } barCategoryGap="15%" barGap={ 2 }>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={ xKey } tick={ { fontSize: 12 } } />
        <YAxis tick={ { fontSize: 12 } } />
        <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff' } } />
        <Legend />
        { yKeys.map( ( yk, i ) => (
          <Bar key={ yk } dataKey={ yk } fill={ COLORS[i % COLORS.length] } name={ yk } />
        ) ) }
      </BarChart>
    );
  };

  const renderBody = () => {
    if ( isExecuting ) {
      return (
        <div className={ styles.chartPanel__empty }>
          <Icon icon={ AiOutlineLoading } className={ styles.treeSpinner } />
          <span>Running query...</span>
        </div>
      );
    }

    if ( !result ) {
      return (
        <div className={ styles.chartPanel__empty }>
          <span>Run a query to see chart</span>
        </div>
      );
    }

    if ( result.error ) {
      return (
        <div className={ styles.chartPanel__empty }>
          <span>{ result.error }</span>
        </div>
      );
    }

    if ( !hasChart ) {
      return (
        <div className={ styles.chartPanel__empty }>
          <span>No chartable data</span>
        </div>
      );
    }

    return (
      <div className={ styles.chartPanel__canvas } ref={ chartRef }>
        { truncated && (
          <div className={ styles.chartPanel__truncatedNotice }>
            Showing first 1,000 of { result.rows.length.toLocaleString() } rows
          </div>
        ) }
        <ResponsiveContainer width="100%" height="100%" minHeight={ 50 } debounce={ 100 }>
          { renderChart()! }
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className={ styles.chartPanel } data-testid="chart-panel">
      <div className={ styles.chartPanel__header }>
        <div className={ styles.chartPanel__title }>
          <button
            type="button"
            data-testid="chart-collapse-btn"
            className={ styles.chartPanel__collapseBtn }
            onClick={ () => setCollapsed( c => !c ) }
          >
            { collapsed ? <FiChevronRight size={ 14 } /> : <FiChevronDown size={ 14 } /> }
          </button>
          Chart
        </div>
        { !collapsed && hasChart && !isExecuting && (
          <div className={ styles.chartPanel__toolbar }>
            <select
              className={ styles.chartPanel__typeSelect }
              data-testid="chart-type-select"
              value={ activeType }
              onChange={ e => setChartTypeOverride( e.target.value as ChartType ) }
              title="Chart type"
            >
              { chartTypeOptions.map( opt => (
                <option key={ opt.value } value={ opt.value }>{ opt.label }</option>
              ) ) }
            </select>
            <select
              className={ styles.chartPanel__typeSelect }
              data-testid="chart-x-axis-select"
              value={ xKeyOverride ?? config?.xKey ?? '' }
              onChange={ e => setXKeyOverride( e.target.value ) }
              title="X-axis column"
            >
              { allColumns.map( col => (
                <option key={ col } value={ col }>X: { col }</option>
              ) ) }
            </select>
          </div>
        ) }
      </div>
      { !collapsed && renderBody() }
    </div>
  );
}

export default memo( ChartPanel );
